'use client';

import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Autocomplete,
  Box,
  Chip,
  CircularProgress,
  TextField,
  Tooltip,
} from '@/primitives';
import BarChartIcon from '@mui/icons-material/BarChart';
import {
  alpha,
  useTheme,
  type SxProps,
  type Theme,
} from '@mui/material/styles';
import { setDefaultHeader } from '@/publicSearch/graphql/headers';
import { useQuickSearchAnimalEbvsLazyQuery } from '@/publicSearch/graphql/queries.generated';
import usePublicSearchContext from '@/publicSearch/store/usePublicSearchContext';
import { debounce } from 'lodash';
import { quickSearchInputSx } from '../search/constants';
import {
  QUICK_SEARCH_MIN_LENGTH,
  QUICK_SEARCH_TOP_RESULTS,
  QUICK_SEARCH_TYPEAHEAD_DEBOUNCE_MS,
} from '../search/quickSearchConfig';
import { IDENT_CHIP_PLACEHOLDER } from '../search/quickSearchCopy';
import {
  getWildcardLabel,
  isMessageOption,
  isWildcardOption,
  resolveBlurWildcardPattern,
  resolveExplicitWildcards,
  resolveWildcard,
  type MessageOption,
  type WildcardKind,
  type WildcardOption,
} from '../search/identChipWildcardUi';
import { normalizeWildcardInput } from '../search/animalIdentChipFilter';
import { autocompleteSizeSx } from './autocompleteSizeSx';
import { toSxArray } from '@/lib/sx';
import type { AnimalChipOption, SireDamChipValue } from './types';

/**
 * Small icon shown next to each option/chip indicating whether the animal
 * has any EBV data. Coloured (primary) when present, greyed when not — the
 * icon is always rendered so absence-of-EBVs is a visible, distinct state
 * rather than an ambiguous gap.
 */
function EbvDataIcon({
  hasEbvData,
  size = 14,
  sx,
}: {
  hasEbvData: boolean;
  size?: number;
  sx?: SxProps<Theme>;
}) {
  return (
    <Tooltip title={hasEbvData ? 'EBV data available' : 'No EBV data'} arrow>
      <Box
        component="span"
        aria-label={hasEbvData ? 'Has EBV data' : 'No EBV data'}
        sx={[
          {
            display: 'inline-flex',
            alignItems: 'center',
            flexShrink: 0,
          },
          ...toSxArray(sx),
        ]}
      >
        <BarChartIcon
          sx={{
            fontSize: size,
            color: hasEbvData ? 'primary.main' : 'action.disabled',
          }}
        />
      </Box>
    </Tooltip>
  );
}

function getChipLabel(chip: SireDamChipValue): string {
  if (typeof chip === 'string') return chip;
  const ident = chip.animalIdent ?? '';
  const name = chip.animalName ?? '';
  if (ident && name) return `${ident} - ${name}`;
  return ident || name || '';
}

function serializeChips(chips: SireDamChipValue[]): string {
  return chips
    .map((chip) => (typeof chip === 'string' ? chip : (chip.animalIdent ?? '')))
    .filter(Boolean)
    .join(',');
}

function parseValueToChips(value: string): SireDamChipValue[] {
  return value
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);
}

interface AnimalIdentMultiSelectProps {
  label?: string;
  placeholder?: string;
  value: string;
  onChange: (value: string) => void;
  inputRef?: React.Ref<HTMLInputElement>;
  disabled?: boolean;
  sx?: SxProps<Theme>;
  /** When set, restricts autocomplete results to animals of this sex ('M' = male/sire, 'F' = female/dam). */
  sexFilter?: 'M' | 'F';
  /** Use with toolbar-style fields (e.g. mating bar) to match small TextField / Select height. */
  size?: 'small' | 'medium';
  /** Announces as required to assistive tech without showing the label asterisk. */
  ariaRequired?: boolean;
  /**
   * When true, plain (non-`%`) input also surfaces explicit "Begins with X"
   * and "Contains X" wildcard options, matching the affordances the legacy
   * single-value quick-search input offered for public search filters.
   */
  showExplicitWildcardOptions?: boolean;
  /** When the user blurs with plain text, commit this wildcard shape (mating uses contains). */
  plainTextBlurWildcard?: WildcardKind;
}

export default function AnimalIdentMultiSelect({
  label,
  placeholder = IDENT_CHIP_PLACEHOLDER,
  value,
  onChange,
  inputRef,
  disabled,
  sx,
  sexFilter,
  size = 'medium',
  ariaRequired = false,
  showExplicitWildcardOptions = false,
  plainTextBlurWildcard,
}: AnimalIdentMultiSelectProps) {
  const theme = useTheme();
  const orgId = usePublicSearchContext((context) => context.society.orgId);
  const [quickSearch, { data, loading }] = useQuickSearchAnimalEbvsLazyQuery();

  const [chipValues, setChipValues] = useState<SireDamChipValue[]>(() =>
    parseValueToChips(value),
  );
  const [inputValue, setInputValue] = useState<string>('');

  // Track last string emitted via onChange so we can distinguish external
  // prop changes (e.g. form clear) from round-trips of our own updates.
  const lastEmittedRef = useRef<string>(value);

  useEffect(() => {
    if (value !== lastEmittedRef.current) {
      // Controlled prop sync when parent clears or replaces the value externally.
      // eslint-disable-next-line react-hooks/set-state-in-effect -- ref-guarded controlled-prop sync: re-parses chips when the parent clears/replaces value externally; chipValues is also set by the Autocomplete onChange, so it cannot be a pure derived value
      setChipValues(parseValueToChips(value));
      lastEmittedRef.current = value;
    }
  }, [value]);

  const options = useMemo(
    () => (data?.quickSearchAnimalEbvs ?? []) as AnimalChipOption[],
    [data?.quickSearchAnimalEbvs],
  );

  const runSearch = useMemo(
    () =>
      debounce((term: string) => {
        const trimmed = term.trim();
        if (trimmed.length < QUICK_SEARCH_MIN_LENGTH || !orgId) return;
        quickSearch({
          variables: {
            searchTerm: trimmed,
            top: QUICK_SEARCH_TOP_RESULTS,
            sex: sexFilter,
          },
          context: { headers: setDefaultHeader(orgId) },
        });
      }, QUICK_SEARCH_TYPEAHEAD_DEBOUNCE_MS),
    [orgId, quickSearch, sexFilter],
  );

  useEffect(() => {
    return () => runSearch.cancel();
  }, [runSearch]);

  const emitChange = useCallback(
    (chips: SireDamChipValue[]) => {
      const serialized = serializeChips(chips);
      lastEmittedRef.current = serialized;
      onChange(serialized);
    },
    [onChange],
  );

  const handleInputChange = useCallback(
    (_: React.SyntheticEvent, newInputValue: string) => {
      // Comma-separated paste → split into chips immediately, no search needed.
      if (newInputValue.includes(',')) {
        const parts = newInputValue
          .split(',')
          .map((s) => s.trim())
          .filter(Boolean);
        if (parts.length > 0) {
          const seen = new Set<string>(
            chipValues.map((c) =>
              typeof c === 'string' ? c : (c.animalIdent ?? ''),
            ),
          );
          const toAdd = parts.filter((p) => !seen.has(p));
          if (toAdd.length > 0) {
            const next = [...chipValues, ...toAdd];
            setChipValues(next);
            emitChange(next);
          }
          setInputValue('');
          runSearch.cancel();
          return;
        }
      }

      setInputValue(newInputValue);
      // When the input contains % the user is entering a wildcard pattern —
      // don't hit the search API; only the synthetic wildcard option will show.
      if (normalizeWildcardInput(newInputValue).includes('%')) {
        runSearch.cancel();
      } else {
        runSearch(newInputValue);
      }
    },
    [chipValues, emitChange, runSearch],
  );

  const handleChange = useCallback(
    (
      _: React.SyntheticEvent,
      newValues: (SireDamChipValue | WildcardOption | MessageOption)[],
    ) => {
      // Skip message items, convert wildcard options to their pattern string, then dedup.
      const seen = new Set<string>();
      const deduped: SireDamChipValue[] = [];
      for (const item of newValues) {
        if (isMessageOption(item)) continue;
        const normalized: SireDamChipValue = isWildcardOption(item)
          ? item.pattern
          : item;
        const key =
          typeof normalized === 'string'
            ? normalized.trim()
            : (normalized.animalIdent ?? '');
        if (!key || seen.has(key)) continue;
        seen.add(key);
        deduped.push(
          typeof normalized === 'string' ? normalized.trim() : normalized,
        );
      }
      setChipValues(deduped);
      emitChange(deduped);
    },
    [emitChange],
  );

  const commitPendingInputOnBlur = useCallback(() => {
    const pattern = resolveBlurWildcardPattern(
      inputValue,
      plainTextBlurWildcard,
    );
    if (!pattern) return;

    const seen = new Set(
      chipValues
        .map((c) => (typeof c === 'string' ? c.trim() : (c.animalIdent ?? '')))
        .filter(Boolean),
    );
    if (seen.has(pattern)) {
      setInputValue('');
      return;
    }

    const next = [...chipValues, pattern];
    setChipValues(next);
    emitChange(next);
    setInputValue('');
  }, [chipValues, emitChange, inputValue, plainTextBlurWildcard]);

  return (
    <Autocomplete<SireDamChipValue, true, false, true>
      multiple
      freeSolo
      slotProps={{
        paper: {
          sx: {
            '& .MuiAutocomplete-option': { fontSize: '13px' },
            '& .MuiAutocomplete-loading': { fontSize: '13px' },
            '& .MuiAutocomplete-noOptions': { fontSize: '13px' },
          },
        },
      }}
      openOnFocus={false}
      noOptionsText=""
      value={chipValues}
      inputValue={inputValue}
      onInputChange={handleInputChange}
      onChange={handleChange as any}
      options={options as SireDamChipValue[]}
      filterOptions={(baseOptions) => {
        const term = normalizeWildcardInput(inputValue).trim();
        const hasPercent = term.includes('%');
        // When % is present suppress actual results — only the wildcard option shows.
        // Without %, only offer the wildcard alongside real results. Short input returns
        // [] so we do not show hint rows; after a real search with no hits, show one line.
        const result: (SireDamChipValue | WildcardOption | MessageOption)[] =
          hasPercent ? [] : [...baseOptions];

        // Public-search variant: surface both "Begins with X" and "Contains X"
        // when the user types a plain term, so they don't need to know about
        // `%`. The mating-predictor (default) variant keeps its existing
        // single-suggestion behaviour to avoid cluttering the bar.
        if (showExplicitWildcardOptions && !hasPercent) {
          for (const w of resolveExplicitWildcards(term)) result.push(w);
        } else {
          const wildcard = resolveWildcard(term);
          if (wildcard && (hasPercent || baseOptions.length > 0))
            result.push(wildcard);
        }
        if (result.length === 0 && !loading) {
          const base = term.replace(/^%+|%+$/g, '').trim();
          const tooShortForWildcard =
            hasPercent && base.length < QUICK_SEARCH_MIN_LENGTH;
          const tooShortForSearch =
            !hasPercent && term.length < QUICK_SEARCH_MIN_LENGTH;
          if (
            !tooShortForWildcard &&
            !tooShortForSearch &&
            term.length >= QUICK_SEARCH_MIN_LENGTH
          ) {
            result.push({ _isMessage: true, text: 'No animals found' });
          }
        }
        return result as SireDamChipValue[];
      }}
      getOptionLabel={(option) => {
        if (isWildcardOption(option)) return option.pattern;
        if (isMessageOption(option)) return option.text;
        if (typeof option === 'string') return option;
        return getChipLabel(option);
      }}
      getOptionDisabled={(option) => isMessageOption(option as unknown)}
      isOptionEqualToValue={(option, val) => {
        if (typeof option === 'string' && typeof val === 'string')
          return option === val;
        if (typeof option !== 'string' && typeof val !== 'string')
          return (
            (option as AnimalChipOption).animalId ===
            (val as AnimalChipOption).animalId
          );
        return false;
      }}
      disabled={disabled}
      loading={loading}
      sx={[
        quickSearchInputSx,
        {
          flex: 1,
          minWidth: 160,
          // MUI Autocomplete medium applies `padding: 9px` via the more-specific
          // `.MuiOutlinedInput-root.MuiAutocomplete-inputRoot` selector (two classes
          // beats our single `.MuiInputBase-root` class). Use the same two-class
          // selector so our padding wins without needing !important, then set
          // height:auto so the box grows when chips wrap to a second line.
          ...autocompleteSizeSx(size),
        },
        ...toSxArray(sx),
      ]}
      renderValue={(tagValue, getItemProps) =>
        tagValue.map((option, index) => {
          const { key, ...tagProps } = getItemProps({ index });
          // Wildcard / free-text chips are plain strings — no EBV icon for them.
          // Resolved animal chips always show the icon (active when isEbvData,
          // greyed otherwise) so the EBV state is visible after selection too.
          const isAnimal = typeof option !== 'string';
          const ebvIcon = isAnimal ? (
            <EbvDataIcon
              hasEbvData={option.isEbvData === true}
              size={size === 'small' ? 12 : 14}
              sx={{ pl: '6px', pr: '2px' }}
            />
          ) : undefined;
          return (
            <Chip
              key={key}
              icon={ebvIcon}
              label={
                typeof option === 'string' ? option : (option.animalIdent ?? '')
              }
              size="small"
              sx={(theme) => ({
                // Chip uses `background.default`, which is intrinsically one
                // step darker than the dialog/hero `background.paper` surface
                // in both schemes (dark: slate[900] vs slate[800], light:
                // slate[50] vs white), so it reads as the inverse element
                // against the lighter input — and the css-var token swaps
                // automatically with `data-mui-color-scheme`.
                backgroundColor: (theme.vars ?? theme).palette.background
                  .default,
                ...(size === 'small'
                  ? {
                      height: 22,
                      '& .MuiChip-label': { px: 0.75, fontSize: '12px' },
                      '& .MuiChip-icon': { ml: '6px', mr: '2px' },
                    }
                  : { '& .MuiChip-icon': { ml: '6px', mr: '2px' } }),
              })}
              {...tagProps}
            />
          );
        })
      }
      renderInput={(params) => (
        <TextField
          {...params}
          size={size}
          label={label}
          placeholder={chipValues.length === 0 ? placeholder : undefined}
          inputRef={inputRef}
          slotProps={{
            ...params.slotProps,
            htmlInput: {
              ...params.slotProps.htmlInput,
              ...(ariaRequired ? { 'aria-required': true as const } : {}),
              onBlur: (e: React.FocusEvent<HTMLInputElement>) => {
                params.slotProps.htmlInput?.onBlur?.(e);
                const rt = e.relatedTarget as HTMLElement | null;
                if (rt?.closest?.('.MuiAutocomplete-listbox')) return;
                commitPendingInputOnBlur();
              },
            },
            input: {
              ...params.slotProps.input,
              endAdornment: (
                <>
                  {loading ? (
                    <CircularProgress
                      color="inherit"
                      size={size === 'small' ? 16 : 20}
                    />
                  ) : null}
                  {params.slotProps.input.endAdornment}
                </>
              ),
            },
          }}
        />
      )}
      renderOption={(props, option) => {
        const { key, ...optionProps } = props;
        if (isMessageOption(option)) {
          return (
            <li
              key={key}
              {...optionProps}
              style={{
                pointerEvents: 'none',
                color: alpha(theme.palette.text.primary, 0.38),
                fontStyle: 'italic',
                fontSize: '13px',
              }}
            >
              {option.text}
            </li>
          );
        }
        if (isWildcardOption(option)) {
          return (
            <li key={key} {...optionProps} style={{ fontStyle: 'italic' }}>
              {getWildcardLabel(option)}
            </li>
          );
        }
        const optLabel =
          typeof option === 'string'
            ? option
            : getChipLabel(option as AnimalChipOption);
        // Free-text/wildcard options don't carry EBV info — only render the
        // icon for resolved animal options so absence-of-data is unambiguous.
        const isAnimal = typeof option !== 'string';
        const hasEbvData =
          isAnimal && (option as AnimalChipOption).isEbvData === true;
        return (
          <li
            key={key}
            {...optionProps}
            style={{ display: 'flex', alignItems: 'center', gap: 8 }}
          >
            {isAnimal ? <EbvDataIcon hasEbvData={hasEbvData} /> : null}
            <span style={{ flex: 1, minWidth: 0 }}>{optLabel}</span>
          </li>
        );
      }}
    />
  );
}
